import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash, ArrowClockwise, Camera } from "@phosphor-icons/react";

const EMPTY = {
  email: "", password: "", name: "", phone: "", photo_url: "", bio: "",
  languages: "", specializations: "", role: "agent", active: true,
};

function csv(arr) { return Array.isArray(arr) ? arr.join(", ") : (arr || ""); }
function fromCsv(s) { return String(s || "").split(",").map((v) => v.trim()).filter(Boolean); }

export default function Agents() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // agent id when editing, null when creating
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState("");
  const fileRef = useRef();

  async function load() {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/agents");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load agents");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditing(null);
    setF(EMPTY);
    setOpen(true);
  }
  function openEdit(a) {
    setEditing(a.id);
    setF({
      email: a.email || "",
      password: "",
      name: a.name || "",
      phone: a.phone || "",
      photo_url: a.photo_url || "",
      bio: a.bio || "",
      languages: csv(a.languages),
      specializations: csv(a.specializations),
      role: a.role || "agent",
      active: a.active !== false,
    });
    setOpen(true);
  }

  async function submit(e) {
    e.preventDefault();
    const payload = {
      ...f,
      languages: fromCsv(f.languages),
      specializations: fromCsv(f.specializations),
    };
    // Drop empty password on edit — backend treats missing password as "unchanged"
    if (editing && !payload.password) delete payload.password;

    try {
      if (editing) {
        await api.put(`/admin/agents/${editing}`, payload);
        toast.success("Agent updated");
      } else {
        if (!payload.password) return toast.error("Password is required for new agents");
        await api.post("/admin/agents", payload);
        toast.success("Agent created");
      }
      setOpen(false);
      setEditing(null);
      setF(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  }

  async function remove(a) {
    if (!confirm(`Delete ${a.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/agents/${a.id}`);
      toast.success("Agent removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }

  async function uploadPhoto() {
    if (!editing) return toast.error("Save the agent first, then upload the photo");
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a photo file");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/agents/${editing}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF((prev) => ({ ...prev, photo_url: data.photo_url }));
      toast.success("Photo uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  }

  const filtered = items.filter((a) => {
    if (!q) return true;
    const hay = `${a.name} ${a.email} ${a.phone || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  return (
    <div className="p-6 lg:p-10" data-testid="admin-agents">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Team · {items.length}</div>
          <h1 className="font-serif text-3xl lg:text-4xl">Agents &amp; Admins</h1>
        </div>
        <div className="flex items-center gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email…" className={`${inputCls} w-56`} data-testid="agents-search" />
          <button onClick={load} className="border border-[#E5E7EB] px-3 py-2 text-sm hover:border-[#001588]" data-testid="agents-refresh"><ArrowClockwise size={16} /></button>
          <button onClick={openCreate} className="flex items-center gap-2 bg-[#001588] text-white px-5 py-3 text-sm hover:bg-black transition-colors" data-testid="agents-new">
            <Plus size={16} /> New agent
          </button>
        </div>
      </div>

      {busy && <div className="text-neutral-500 text-sm mb-4">Loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((a) => (
          <div key={a.id} className="border border-[#E5E7EB] bg-white p-6 group relative" data-testid={`agent-row-${a.id}`}>
            <div className="flex gap-4 items-center mb-4">
              {a.photo_url ? (
                <img src={a.photo_url} alt={a.name} className="w-14 h-14 object-cover rounded-full" />
              ) : (
                <div className="w-14 h-14 bg-[#ffffff] flex items-center justify-center rounded-full text-[#001588] font-serif">{(a.name || "?")[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg truncate">{a.name}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  {a.role === "founding_partner" ? "Founding Partner" : a.role}
                  {a.active === false && <span className="text-[10px] uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5">Inactive</span>}
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-600 truncate" title={a.email}>{a.email}</div>
            {a.phone && <div className="text-sm text-gray-600">{a.phone}</div>}
            {a.specializations?.length > 0 && (
              <div className="text-xs text-gray-500 mt-3 line-clamp-2">{a.specializations.join(" · ")}</div>
            )}
            {a.languages?.length > 0 && (
              <div className="text-xs text-gray-400 mt-1">Speaks: {a.languages.join(", ")}</div>
            )}
            <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-100">
              <button onClick={() => openEdit(a)} className="flex-1 flex items-center justify-center gap-1 text-xs uppercase tracking-wider py-2 border border-neutral-200 hover:border-[#001588] hover:text-[#001588]" data-testid={`agent-edit-${a.id}`}>
                <Pencil size={12} /> Edit
              </button>
              <button onClick={() => remove(a)} className="flex items-center justify-center gap-1 text-xs uppercase tracking-wider py-2 px-3 border border-neutral-200 hover:border-red-500 hover:text-red-500" data-testid={`agent-delete-${a.id}`}>
                <Trash size={12} />
              </button>
            </div>
          </div>
        ))}
        {!busy && filtered.length === 0 && (
          <div className="col-span-full text-center text-neutral-500 py-14">No agents match your search.</div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)} data-testid="agent-modal">
          <form onSubmit={submit} className="w-full max-w-2xl bg-white p-6 lg:p-8 my-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-6">{editing ? "Edit agent" : "Add agent"}</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Name *</span>
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className={inputCls} data-testid="agent-name" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Email *</span>
                <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className={inputCls} data-testid="agent-email" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">{editing ? "New password (leave blank to keep)" : "Password *"}</span>
                <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required={!editing} className={inputCls} data-testid="agent-password" autoComplete="new-password" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Phone</span>
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="agent-phone" placeholder="+971 55 …" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Bio</span>
                <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows="3" className={inputCls} data-testid="agent-bio" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Languages (comma sep)</span>
                <input value={f.languages} onChange={(e) => setF({ ...f, languages: e.target.value })} className={inputCls} data-testid="agent-languages" placeholder="English, Arabic, Russian" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Specialisations (comma sep)</span>
                <input value={f.specializations} onChange={(e) => setF({ ...f, specializations: e.target.value })} className={inputCls} data-testid="agent-specializations" placeholder="Palm Jumeirah, Off-plan" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Role</span>
                <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inputCls} data-testid="agent-role">
                  <option value="agent">Agent</option>
                  <option value="founding_partner">Founding Partner</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="agent-active" />
                <span className="text-sm text-gray-700">Active (can log in)</span>
              </label>
            </div>

            {editing && (
              <div className="mt-6 border-t border-neutral-200 pt-6">
                <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-3">Headshot</div>
                <div className="flex items-center gap-4">
                  {f.photo_url ? (
                    <img src={f.photo_url} alt="preview" className="w-16 h-16 object-cover rounded-full" />
                  ) : (
                    <div className="w-16 h-16 bg-[#ffffff] rounded-full flex items-center justify-center text-neutral-400"><Camera size={20} /></div>
                  )}
                  <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="text-sm" data-testid="agent-photo-file" />
                  <button type="button" onClick={uploadPhoto} className="px-4 py-2 border border-[#001588] text-[#001588] text-xs uppercase tracking-wider hover:bg-[#001588] hover:text-white" data-testid="agent-photo-upload">Upload</button>
                </div>
                <label className="block mt-3">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block mb-1">Or paste URL</span>
                  <input value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} className={inputCls} data-testid="agent-photo-url" placeholder="https://…/photo.jpg" />
                </label>
              </div>
            )}

            <div className="flex gap-2 mt-8">
              <button type="submit" className="bg-[#001588] text-white px-6 py-2.5 text-sm hover:bg-black" data-testid="agent-save">{editing ? "Save changes" : "Create agent"}</button>
              <button type="button" onClick={() => setOpen(false)} className="border border-gray-300 px-6 py-2.5 text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
